import { FormikProps } from "formik";
import { useMemo } from "react";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import type { Option } from "@/constants/common";
import { GROUP_FIELDS_LABEL } from "../group-visit-constant";
import { GroupDetail, GroupFieldsValues } from "../group-visit-types";
import { getGroupTherapistIds } from "../group-visit-utils";
import GroupFormsSection from "./GroupFormsSection";
import GroupScheduleFields from "./GroupScheduleFields";
import SessionCapacityCard from "./SessionCapacityCard";
import TherapistCard from "./TherapistCard";

export type GroupFormFieldsProps = {
  formik: FormikProps<GroupFieldsValues>;
  /** Selectable groups; the caller owns fetching them. */
  groupOptions?: Option[];
  isGroupsLoading?: boolean;
  /** Group record is being fetched to prefill the fields. */
  isGroupDetailLoading?: boolean;
  /** Chosen group's record; its care team is shown read-only. */
  selectedGroup?: GroupDetail;
  disabled?: boolean;
};

/**
 * Group visit scheduling fields, ready to drop into any Formik form.
 *
 * @author Rajkumar Rathod
 */
const GroupFormFields = ({
  formik,
  groupOptions = [],
  isGroupsLoading,
  isGroupDetailLoading,
  selectedGroup,
  disabled,
}: GroupFormFieldsProps) => {
  const { values, errors, touched, setFieldValue, handleBlur } = formik;
  const isFieldDisabled = disabled || isGroupDetailLoading;
  const providerIds = useMemo(
    () => getGroupTherapistIds(selectedGroup),
    [selectedGroup]
  );

  return (
    <Flex flexDirection="column" gap={2}>
      <CustomSelect
        label={GROUP_FIELDS_LABEL.groupName}
        required
        name="groupId"
        options={groupOptions}
        value={values.groupId}
        disabled={disabled || isGroupsLoading}
        handleBlur={handleBlur}
        onChange={(value) => setFieldValue("groupId", value)}
        error={touched.groupId && errors.groupId}
      />

      <GroupScheduleFields
        formik={formik}
        providerIds={providerIds}
        disabled={isFieldDisabled}
      />

      <SessionCapacityCard group={selectedGroup} />

      <TherapistCard group={selectedGroup} />

      <GroupFormsSection formik={formik} disabled={disabled} />
    </Flex>
  );
};

export default GroupFormFields;
